import { useCurrentUser } from 'hooks/selectors';
import { useDeviceInfo } from 'hooks/useDeviceInfo';
import { Vehicle } from 'types/vehicle';
import { formatCurrency } from 'utils';
import { SellerWithTooltip, UserWithTooltip } from '../UserWithTooltip';
import { User } from 'types/user';
import moment from 'moment';
import { VEHICLE_STATUSES } from 'constants/vehicleStatuses';
import { DATE_FORMAT } from 'constants/index';
import { useHighestBid } from '../../hooks';

export type InfoItem = {
  label: string;
  value: string | JSX.Element;
  color?: string;
  highlighted?: boolean;
  background?: string;
};

// Matches the winner highlight color used on the admin/auctions/:id page (BidOfferRows.tsx)
const WINNER_HIGHLIGHT_COLOR = 'rgba(253, 183, 20, 0.30)';

const resolveListerName = (lister?: User) => {
  if (!lister) return 'N/A';
  if (lister.role === 'seller') return lister.dealership_name;
  return lister.options?.name_for_bio || lister.first_name || 'N/A';
};

const resolveListingTime = (vehicle: Vehicle) => {
  if (!vehicle.created_at) return '';
  const formatted = moment(vehicle.created_at).format('MM/DD/YY hh:mm A');
  return `(${formatted})`;
};

export const useInfoItems = (vehicle: Vehicle): Record<string, InfoItem> => {
  const { isMobile } = useDeviceInfo();
  const user = useCurrentUser();

  const isAdmin = user.job_title === 'Admin';
  const showUserTooltip = isAdmin && !isMobile;

  const isAwarded = vehicle.status === VEHICLE_STATUSES.AWARDED;
  const showWinnerHighlight = isAwarded && isAdmin;
  const highestBid = useHighestBid(vehicle);

  const highBidderUser = showWinnerHighlight ? vehicle.awarded_user : highestBid.bidder;
  const highBidderName = showWinnerHighlight
    ? vehicle.awarded_user?.dealership_name || 'N/A'
    : highestBid.bidder?.dealership_name || 'N/A';
  const highBidAmount = showWinnerHighlight ? vehicle.vehicle_price : highestBid.amount;

  const showBuyerTooltip = showUserTooltip && highBidderUser;

  const vin = {
    label: 'VIN:',
    value: (
      <div>
        <span>{`${vehicle.vin.slice(0, -6)}`}</span>
        <span style={{ fontWeight: '700', color: 'rgb(57, 148, 222)   ' }}>{`${vehicle.vin.slice(-6)}`}</span>
      </div>
    ),
    highlighted: true
  };
  const seller = {
    label: 'Seller:',
    value: showUserTooltip ? <SellerWithTooltip user={vehicle.user} /> : vehicle.user?.dealership_name || 'N/A'
  };
  const highBidder = {
    label: 'High Bidder:',
    value: showBuyerTooltip ? <UserWithTooltip user={highBidderUser} /> : highBidderName,
    background: showWinnerHighlight ? WINNER_HIGHLIGHT_COLOR : undefined
  };
  const highBid = {
    label: 'High Bid:',
    value: formatCurrency(highBidAmount || 0),
    background: showWinnerHighlight ? WINNER_HIGHLIGHT_COLOR : undefined
  };
  const lastRanDate = vehicle.last_ran_at || vehicle.created_at;
  const lastRan = isAwarded
    ? {
        label: 'Date Sold:',
        value: vehicle.date_status ? moment(vehicle.date_status).format(DATE_FORMAT) : 'N/A'
      }
    : {
        label: 'Last Ran:',
        value: lastRanDate ? moment(lastRanDate).format(DATE_FORMAT) : 'N/A'
      };
  const proxy = {
    label: 'Proxy:',
    value: formatCurrency(vehicle.proxy_bid?.amount || 0)
  };
  const lister = {
    label: 'Lister',
    value: resolveListerName(vehicle.lister) + ' ' + resolveListingTime(vehicle)
  };
  const auctionState = {
    label: 'Auction State',
    value: vehicle.auction?.status || 'N/A',
    color: vehicle.auction?.status === 'active' ? 'rgba(71, 151, 71, 1)' : 'rgba(175, 41, 48, 1)'
  };

  return { vin, seller, highBidder, highBid, lastRan, proxy, lister, auctionState };
};

const getSellerSplitItems = (items: Record<string, InfoItem>) => {
  return [
    [items.vin, items.lister, items.auctionState],
    [items.highBidder, items.highBid, items.lastRan]
  ];
};

export const usePortraitItems = (vehicle: Vehicle) => {
  const items = useInfoItems(vehicle);
  const user = useCurrentUser();
  if (user.role === 'seller') {
    const split = getSellerSplitItems(items);
    return split.flat();
  }
  return Object.values(items);
};

export const useLandscapeItems = (vehicle: Vehicle) => {
  const items = useInfoItems(vehicle);
  const user = useCurrentUser();

  if (user.role === 'seller') {
    return getSellerSplitItems(items);
  }

  return [
    [items.vin, items.seller, items.highBidder, items.highBid, items.lastRan],
    [items.proxy, items.lister, items.auctionState]
  ];
};

export const useDesktopItems = (vehicle: Vehicle) => {
  const items = useInfoItems(vehicle);
  const user = useCurrentUser();

  if (user.role === 'seller') {
    return getSellerSplitItems(items);
  }

  return [
    [items.vin, items.seller, items.lister, items.auctionState],
    [items.proxy, items.highBidder, items.highBid, items.lastRan]
  ];
};
